/**
 * The controls (docs/design/visual-overhaul.md §7.1, §7.2, §7.22; WP3):
 * buttons, icon buttons, segmented control, switch, spinner and the Latin
 * isolate. Split out of ./kit.tsx, which re-exports them.
 */
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from 'react';
import { Icon } from '@neosocial/ui/icon';
import type { IconName } from './icons';

type BtnKind =
  'primary' | 'mode' | 'ghost' | 'secondary' | 'quiet' | 'danger' | 'danger-quiet' | 'link';

/** A long label truncates; the full text shows as the button's title. */
function titleWhenCut(e: { currentTarget: HTMLElement }): void {
  const b = e.currentTarget;
  const l = b.querySelector<HTMLElement>('.lbl');
  if (!l || b.hasAttribute('title')) return;
  if (l.scrollWidth > l.clientWidth) b.title = l.textContent ?? '';
}

export function Btn({
  kind = 'ghost',
  size,
  icon,
  busy,
  children,
  className,
  ...rest
}: {
  kind?: BtnKind;
  /** Default lg 40; 'sm' (or 'md') is 36; 'xs' is the spec's sm 32 (§7.1). */
  size?: 'lg' | 'md' | 'sm' | 'xs';
  icon?: IconName;
  busy?: boolean;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={['nx-btn', kind, size, className].filter(Boolean).join(' ')}
      aria-busy={busy || undefined}
      onPointerEnter={titleWhenCut}
      {...rest}
    >
      {busy ? <i className="nx-spin" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children !== undefined ? <span className="lbl">{children}</span> : null}
    </button>
  );
}

export function IconBtn({
  icon,
  label,
  tipPos,
  size = 'sm',
  flat = true,
  pressed,
  className,
  children,
  ...rest
}: {
  icon: IconName;
  label: string;
  tipPos?: 'top' | 'bottom' | 'start' | 'end';
  /** '' is 40, 'sm' 32 (the default), 'xs' 24 with a 32 hit area, 'lg' 48 (§7.2). */
  size?: 'xs' | 'sm' | 'lg' | '';
  flat?: boolean;
  pressed?: boolean;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={['nx-ibtn', size, flat ? 'flat' : '', className].filter(Boolean).join(' ')}
      aria-label={label}
      data-tip={label}
      data-tip-pos={tipPos}
      aria-pressed={pressed}
      {...rest}
    >
      <Icon name={icon} />
      {children}
    </button>
  );
}

export interface SegOption<V extends string> {
  value: V;
  label: string;
  icon?: IconName;
  lang?: string;
}

export function Seg<V extends string>({
  options,
  value,
  onChange,
  label,
  size = 'sm',
  phoneList,
}: {
  options: SegOption<V>[];
  value: V;
  onChange(v: V): void;
  label: string;
  size?: 'sm' | '';
  /** On a phone the options stand one under another (labels too long to share a row). */
  phoneList?: boolean;
}) {
  const k = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const n = options.length;
  const track = useRef<HTMLDivElement>(null);
  // the thumb on whole CSS pixels (T3): a track's thirds are fractional, so
  // its start and width are rounded from the measured track (CSS falls back
  // to the exact fraction where nothing measures, as in tests). The layout
  // width, not the drawn one: measured in a dialog's scale-in, the drawn
  // width left the thumb 4% short and off its option until the next pick
  useLayoutEffect(() => {
    const el = track.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const place = () => {
      const w = el.offsetWidth - 4;
      if (w <= 0) return;
      const a = Math.round((k * w) / n);
      const b = Math.round(((k + 1) * w) / n);
      el.style.setProperty('--seg-x', `${a}px`);
      el.style.setProperty('--seg-w', `${b - a}px`);
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => ro.disconnect();
  }, [k, n]);
  const pick = (i: number, el?: HTMLElement | null) => {
    const o = options[(i + n) % n];
    if (!o) return;
    onChange(o.value);
    const btn = el?.parentElement?.querySelectorAll('button')[(i + n) % n];
    btn?.focus();
  };
  return (
    <div
      ref={track}
      className={'nx-seg' + (size ? ' ' + size : '') + (phoneList ? ' phone-list' : '')}
      role="radiogroup"
      aria-label={label}
      style={{ '--n': n, '--k': k } as CSSProperties}
      onKeyDown={(e) => {
        const fwd = 'ArrowRight';
        const back = 'ArrowLeft';
        if (e.key === fwd || e.key === 'ArrowDown') {
          e.preventDefault();
          pick(k + 1, e.target as HTMLElement);
        } else if (e.key === back || e.key === 'ArrowUp') {
          e.preventDefault();
          pick(k - 1, e.target as HTMLElement);
        }
      }}
    >
      <span className="thumb" aria-hidden="true" />
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={i === k}
          tabIndex={i === k ? 0 : -1}
          lang={o.lang}
          onClick={() => onChange(o.value)}
        >
          {o.icon ? <Icon name={o.icon} /> : null}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange(v: boolean): void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="nx-sw"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Spinner() {
  return <i className="nx-spin" aria-hidden="true" />;
}

/** A Latin isolate for latency, addresses and codes inside RTL text. */
export function Ltr({ children, cls }: { children: ReactNode; cls?: string }) {
  return (
    <span className={'nx-ltr' + (cls ? ' ' + cls : '')} dir="ltr">
      {children}
    </span>
  );
}

/**
 * The select (§7.22): a drop-in for `<select>` with the same props and
 * `<option>` children, drawn as a field with our own listbox; the hidden
 * native control keeps `onChange(e => e.target.value)` working.
 */
export { SelectBox as Select, Range, rangeFill } from '@neosocial/ui/select';

/**
 * An empty state (§7.27): a 64 tile with a 32 icon, the title, a hint of
 * at most 46ch and an optional action, centred at 40% from the top.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
  compact,
  className,
}: {
  icon: IconName;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={['nx-empty', compact ? 'compact' : '', className].filter(Boolean).join(' ')}>
      <span className="nx-empty-ic" aria-hidden="true">
        <Icon name={icon} size="xl" />
      </span>
      <p className="nx-empty-t">{title}</p>
      {hint ? <p className="nx-empty-b">{hint}</p> : null}
      {action ? <div className="nx-empty-a">{action}</div> : null}
    </div>
  );
}

/**
 * Loading placeholders (§7.27): `message` (a 40 circle and bars at 30, 70
 * and 45%), `channel` (a 16 square and a 55% bar), `member` (a 32 circle
 * and a 50% bar). The shimmer is off under reduced motion.
 */
export function Skeleton({
  kind,
  rows = 1,
}: {
  kind: 'message' | 'channel' | 'member';
  rows?: number;
}) {
  const bar = (w: string | number, h = 12, extra = '') => (
    <i className={'nx-skel' + extra} style={{ width: w, height: h, flex: 'none' }} />
  );
  const one = (i: number) =>
    kind === 'message' ? (
      <div key={i} className="nx-skel-row">
        {bar(40, 40, ' circle')}
        <span className="nx-skel-lines">
          {bar('30%')}
          {bar('70%')}
          {bar('45%')}
        </span>
      </div>
    ) : kind === 'channel' ? (
      <div key={i} className="nx-skel-row">
        {bar(16, 16)}
        {bar('55%')}
      </div>
    ) : (
      <div key={i} className="nx-skel-row">
        {bar(32, 32, ' circle')}
        {bar('50%')}
      </div>
    );
  return (
    <div className="nx-skel-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => one(i))}
    </div>
  );
}
